import { MutationCache, QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";

import { DefaultCatchBoundary } from "./components/DefaultCatchBoundary";
import { NotFound } from "./components/NotFound";
import { isAuthError } from "./lib/auth/auth-error";
import { refreshClerkSessionToken } from "./lib/auth/refresh-clerk-token";
import { reportMutationError } from "./lib/sentry-mutation-reporter";
import { routeTree } from "./routeTree.gen";

const AUTH_RETRY_DELAY_MS = 400;
const DEFAULT_QUERY_RETRIES = 3;

/**
 * On auth errors, refresh the Clerk session token and retry once. This recovers
 * from the "tab was suspended, JWT expired" race that AuthKeepalive can't always
 * win. Only meaningful on the client — during SSR we have no Clerk session to
 * refresh, so we don't retry auth failures there.
 */
// These retry helpers are `function` declarations (not `const` arrows) on
// purpose: they're hoisted, so `getRouter` stays callable even if the start
// handler invokes it while this module is still mid-evaluation during a
// circular-import init window (cold SSR). With `const` arrows that path hits a
// temporal-dead-zone "Cannot access 'queryRetry' before initialization" crash.
// The module-level constants are only read inside these functions (at call
// time, after evaluation completes), never directly during getRouter init.
function isClient(): boolean {
  return typeof window !== "undefined";
}

// TanStack Query passes `failureCount=1` after the first failure, so allow the
// retry while count is still 1 and bail once it reaches 2 (one retry total).
function queryRetry(failureCount: number, error: unknown): boolean {
  if (isAuthError(error)) {
    if (!isClient() || failureCount > 1) return false;
    void refreshClerkSessionToken();
    return true;
  }
  return failureCount < DEFAULT_QUERY_RETRIES;
}

function queryRetryDelay(failureCount: number, error: unknown): number {
  if (isAuthError(error)) return AUTH_RETRY_DELAY_MS;
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

function mutationRetry(failureCount: number, error: unknown): boolean {
  if (!isAuthError(error)) return false;
  if (!isClient() || failureCount > 1) return false;
  void refreshClerkSessionToken();
  return true;
}

function mutationRetryDelay(): number {
  return AUTH_RETRY_DELAY_MS;
}

function createAppRouter() {
  const queryClient = new QueryClient({
    // Per-client cache (a fresh QueryClient is built per SSR request) with a
    // central reporter for every mutation failure — see reportMutationError.
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        reportMutationError(error, mutation.meta),
    }),
    defaultOptions: {
      queries: {
        retry: queryRetry,
        retryDelay: queryRetryDelay,
      },
      mutations: {
        retry: mutationRetry,
        retryDelay: mutationRetryDelay,
      },
    },
  });

  const router = createTanStackRouter({
    routeTree,
    defaultPreload: "intent",
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
    scrollRestoration: true,
    context: { queryClient },
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    // Defaults: handleRedirects: true, wrapQueryClient: true
  });

  return router;
}

type AppRouter = ReturnType<typeof createAppRouter>;

let browserRouter: AppRouter | undefined;

/**
 * The server builds a fresh router and QueryClient for every request. The
 * browser renders one router, and two callers must receive it: `client.tsx`,
 * which gives it to Sentry's navigation tracing, and TanStack Start's
 * `hydrateStart`, which renders it. The server path returns before it reads
 * `browserRouter`, so a cold-SSR call during module evaluation cannot reach
 * that binding while it is still uninitialized. Both browser callers run only
 * after this module has finished evaluating.
 */
export function getRouter(): AppRouter {
  if (!isClient()) return createAppRouter();
  browserRouter ??= createAppRouter();
  return browserRouter;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
